import React, { useEffect, useRef, useState } from "react";
import { Bot, SendHorizontal, Sparkles, UserRound } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { DemoLlmResponse, proxyDemoLlm } from "../../services/demoLlm";
import { RouteType, calculateCost, calculateSavings } from "../../utils/pricing";

interface StepData {
    scenarioId: string;
    stepId: string;
    title: string;
    subtitle: string;
    skipCache?: boolean;
    request: string;
    response: string;
    selectedRoute: RouteType;
    cacheHit: boolean;
    modelUsed: string;
    tokensIn: number;
    tokensOut: number;
    actualCost: number;
    baselineStrongCost: number;
    rationale: string;
    strategy?: string;
}

interface TelemetryEvent {
    scenario_id: string;
    step_id: string;
    selected_route: RouteType;
    cache_hit: boolean;
    model_used: string;
    tokens_in: number;
    tokens_out: number;
    actual_cost: number;
    baseline_strong_cost: number;
    savings_absolute: number;
    savings_percent: number;
}

interface CustomRunResult {
    requestId?: string;
    request: string;
    response: string;
    selectedRoute: RouteType;
    cacheHit: boolean;
    modelUsed: string;
    tokensIn: number;
    tokensOut: number;
    actualCost: number;
    baselineStrongCost: number;
    rationale: string;
    fallbackMode: boolean;
    routerLatencyMs?: number;
    llmResponseTimeMs?: number;
    strategy?: string;
}

interface ChatMessage {
    id: string;
    role: "user" | "assistant" | "guidance";
    content: string;
    requestId?: string;
    route?: RouteType;
    cacheHit?: boolean;
    modelUsed?: string;
    actualCost?: number;
    savingsAbsolute?: number;
    savingsPercent?: number;
    routerLatencyMs?: number;
    llmResponseTimeMs?: number;
    strategy?: string;
}

const PREDEFINED_STEPS: StepData[] = [
    {
        scenarioId: "support_baseline",
        stepId: "baseline_01",
        title: "Scenario 1: FAQ Baseline",
        subtitle: "A buyer asks what the SigLLM Engine actually does for their product.",
        skipCache: true,
        request: "What is the SigLLM Engine and what problem does it solve for an AI product?",
        response:
            "SigLLM Engine is the cost optimization layer that sits in front of your model providers, cuts wasted spend with routing and semantic caching, and keeps your existing app flow intact.",
        selectedRoute: "strong",
        cacheHit: false,
        modelUsed: "claude",
        tokensIn: 40,
        tokensOut: 108,
        actualCost: 0.003072,
        baselineStrongCost: 0.003072,
        rationale: "A category-defining product explainer needs higher-quality synthesis, so the strong path is selected.",
        strategy: "basic_complex",
    },
    {
        scenarioId: "repeated_question_cache",
        stepId: "repeat_01_first",
        title: "Scenario 2: FAQ Integration Question",
        subtitle: "A customer asks how hard it is to adopt SigLLM Engine.",
        skipCache: true,
        request: "Do I need to rewrite my OpenAI integration to use SigLLM Engine?",
        response:
            "No. SigLLM Engine is OpenAI-compatible, so most teams only change the base URL and credentials instead of rebuilding their application logic.",
        selectedRoute: "weak",
        cacheHit: false,
        modelUsed: "chatgpt",
        tokensIn: 32,
        tokensOut: 82,
        actualCost: 0.000235,
        baselineStrongCost: 0.002352,
        rationale: "This is a straightforward adoption question, so the weak model handles it efficiently.",
        strategy: "basic_simple",
    },
    {
        scenarioId: "repeated_question_cache",
        stepId: "repeat_02_cached",
        title: "Scenario 3: FAQ Repeated Ask (Cache Hit)",
        subtitle: "A second buyer asks the same integration question and gets a cached answer.",
        request: "Do I need to rewrite my OpenAI integration to use SigLLM Engine?",
        response:
            "Cache hit: Reusing the validated integration answer for the SigLLM Engine adoption question. No new model call is needed.",
        selectedRoute: "cache",
        cacheHit: true,
        modelUsed: "semantic_cache",
        tokensIn: 32,
        tokensOut: 82,
        actualCost: 0,
        baselineStrongCost: 0.002352,
        rationale: "A repeated product FAQ is served from semantic cache to keep response time and cost near zero.",
        strategy: "cache",
    },
    {
        scenarioId: "simple_request_weak",
        stepId: "weak_01",
        title: "Scenario 4: FAQ Analytics Question",
        subtitle: "A prospect asks what proof they get that SigLLM Engine is saving money.",
        request: "What visibility do I get into savings, usage, and cache hits with SigLLM Engine?",
        response:
            "SigLLM Engine shows usage, spend, savings, latency, and cache-hit trends in one place so teams can prove optimization impact without stitching together provider dashboards.",
        selectedRoute: "weak",
        cacheHit: false,
        modelUsed: "chatgpt",
        tokensIn: 22,
        tokensOut: 70,
        actualCost: 0.000194,
        baselineStrongCost: 0.001944,
        rationale: "This is a direct product capability question, so it stays on the weak path.",
        strategy: "basic_simple",
    },
    {
        scenarioId: "complex_request_strong",
        stepId: "strong_01",
        title: "Scenario 5: FAQ Buyer Evaluation Question",
        subtitle: "A technical buyer asks when SigLLM Engine becomes worth paying for.",
        skipCache: true,
        request:
            "When does SigLLM Engine make sense for a team instead of managing model costs manually?",
        response:
            "SigLLM Engine becomes compelling once LLM spend is painful enough to matter but still fragmented across prompts, providers, and workflows. It is especially valuable for AI startups and SaaS teams that want measurable savings, OpenAI-compatible integration, and a clear path from developer proof to buyer-facing ROI.",
        selectedRoute: "strong",
        cacheHit: false,
        modelUsed: "claude",
        tokensIn: 52,
        tokensOut: 168,
        actualCost: 0.004656,
        baselineStrongCost: 0.004656,
        rationale: "This requires a nuanced value and buying-threshold explanation, so it triggers the strong path.",
        strategy: "basic_complex",
    },
    {
        scenarioId: "call_logs_review",
        stepId: "logs_01",
        title: "Scenario 6: FAQ ROI Question",
        subtitle: "A stakeholder asks how to explain SigLLM Engine ROI internally.",
        request: "How should I explain SigLLM Engine ROI to a CTO, product lead, or finance owner?",
        response:
            "Position SigLLM Engine as cost optimization infrastructure: it reduces wasted LLM spend through routing and semantic caching, preserves the existing app stack, and gives teams savings data they can use to justify expansion.",
        selectedRoute: "weak",
        cacheHit: false,
        modelUsed: "chatgpt",
        tokensIn: 24,
        tokensOut: 86,
        actualCost: 0.000233,
        baselineStrongCost: 0.002352,
        rationale: "This is a concise positioning question, so the weak path is enough.",
        strategy: "basic_simple",
    },
];

const TOTAL_STEPS = PREDEFINED_STEPS.length;

const INITIAL_CHAT_MESSAGE: ChatMessage = {
    id: "assistant-initial",
    role: "assistant",
    content:
        "SigLLM Assistant is ready. Ask a customer-style FAQ about SigLLM Engine and I will route it through weak/strong/cache paths with cost metrics.",
};

const CUSTOM_INITIAL_CHAT_MESSAGES: ChatMessage[] = [
    INITIAL_CHAT_MESSAGE,
    {
        id: "assistant-note",
        role: "assistant",
        content: "I can explain how SigLLM Engine works, where savings come from, and why teams adopt it.",
    },
    {
        id: "assistant-guidance",
        role: "guidance",
        content: `Please send this question to continue: "${PREDEFINED_STEPS[0].request}"`,
    },
];

const buildGuidanceMessage = (content: string): ChatMessage => ({
    id: `guidance-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role: "guidance",
    content,
});

const toRunResult = (step: StepData): CustomRunResult => ({
    requestId: undefined,
    request: step.request,
    response: step.response,
    selectedRoute: step.selectedRoute,
    cacheHit: step.cacheHit,
    modelUsed: step.modelUsed,
    tokensIn: step.tokensIn,
    tokensOut: step.tokensOut,
    actualCost: step.actualCost,
    baselineStrongCost: step.baselineStrongCost,
    rationale: step.rationale,
    strategy: step.strategy,
    fallbackMode: false,
});

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    }).format(value);
};

const formatLatency = (value?: number): string => {
    if (typeof value !== "number" || Number.isNaN(value)) {
        return "n/a";
    }

    if (value < 1000) {
        return `${Math.max(1, Math.round(value))} ms`;
    }

    return `${(value / 1000).toFixed(1)} s`;
};

const isMainModel = (value?: string): boolean => {
    if (!value) {
        return false;
    }

    const normalized = value.trim().toLowerCase();
    return normalized === "main"
        || normalized === "claude"
        || normalized === "main model"
        || normalized === "gpt-5.4"
        || normalized === "gpt-5-4";
};

const formatModelLabel = (value?: string): string => {
    if (!value) {
        return "n/a";
    }

    const normalized = value.trim();
    if (isMainModel(normalized)) {
        return "gpt-5.4";
    }

    const aliasMap: Record<string, string> = {
        chatgpt: "gpt-5-mini",
        mini: "gpt-5-mini",
        semantic_cache: "semantic-cache",
        CACHE: "semantic-cache",
    };

    if (aliasMap[normalized]) {
        return aliasMap[normalized];
    }

    return normalized.replace(/-\d{4}-\d{2}-\d{2}$/u, "");
};

const normalizePrompt = (value: string): string => {
    return value.trim().toLowerCase().replace(/\s+/g, " ");
};

const estimateTokens = (text: string): number => {
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(10, Math.round(words * 1.35));
};

const shortenSupportReply = (text: string): string => {
    const cleaned = text
        .replace(/\r/g, "")
        .replace(/\b(e\.g\.|i\.e\.|etc\.)/gi, (match) => match.replace(/\./g, ""))
        .replace(/^\s*[-*]\s+/gm, "")
        .replace(/^\s*\d+[.)]\s+/gm, "")
        .trim();

    const paragraphs = cleaned
        .split(/\n{2,}/)
        .map((block) => block.replace(/\s+/g, " ").trim())
        .filter(Boolean)
        .filter((block) => !/^(steps|notes and tips|example|example \(chat api\)|practical tips)$/i.test(block));

    const candidate = paragraphs.find((block) => block.length > 0) ?? cleaned.replace(/\s+/g, " ").trim();
    const withoutLeadIn = candidate.replace(/^(short answer|answer|summary)\s*:\s*/i, "");
    const normalized = withoutLeadIn.charAt(0).toUpperCase() + withoutLeadIn.slice(1);

    if (normalized.length <= 190) {
        return normalized;
    }

    const sentenceBoundary = normalized.slice(0, 190).match(/^(.+[.!?])(?:\s|$)/);
    if (sentenceBoundary?.[1]) {
        return sentenceBoundary[1].trim();
    }

    const truncated = normalized.slice(0, 170);
    const lastSpace = truncated.lastIndexOf(" ");
    return `${truncated.slice(0, lastSpace > 0 ? lastSpace : truncated.length).trim()}...`;
};

const getOptimizationLabel = (message: Pick<ChatMessage, "cacheHit" | "strategy" | "route">): "Cache" | "LLM Routing" | "None" => {
    if (message.cacheHit) {
        return "Cache";
    }

    if (message.route === "strong") {
        return "None";
    }

    if (message.strategy) {
        return "LLM Routing";
    }

    return "None";
};

const shouldShowModelLabel = (message: Pick<ChatMessage, "cacheHit">): boolean => !message.cacheHit;

// Pricing functions moved to src/utils/pricing.ts

// Removed extractResponse as we now receive structured responses natively from llmrouter

const isComplexPrompt = (prompt: string): boolean => {
    const lower = prompt.toLowerCase();
    const complexitySignals = [
        "root cause",
        "postmortem",
        "multi-step",
        "rollback",
        "security",
        "latency",
        "incident",
        "compliance",
        "gdpr",
        "soc2",
    ];

    return complexitySignals.some((signal) => lower.includes(signal)) || prompt.trim().split(/\s+/).length > 30;
};

const buildFallbackResponse = (route: RouteType, prompt: string): string => {
    if (route === "strong") {
        return `Fallback analysis: Your request appears complex, so the strong path was selected. Primary action: break the issue into diagnosis, mitigation, and verification phases.\n\nPrompt summary: ${prompt}`;
    }

    return `Fallback support answer: This request maps to a simple support flow and was handled by the weak optimization path.\n\nPrompt summary: ${prompt}`;
};

const normalizeLiveResult = (
    responseData: DemoLlmResponse,
    prompt: string,
    fallbackRoute: RouteType,
    latencyMs?: number
): Omit<CustomRunResult, "fallbackMode" | "rationale" | "request"> => {
    const responseText = shortenSupportReply(responseData.response || "No response text received from router.");
    const tokensIn = responseData.usage?.prompt_tokens || estimateTokens(prompt);
    const tokensOut = responseData.usage?.completion_tokens || estimateTokens(responseText);
    const reportedRoute = responseData.route || fallbackRoute;
    const cacheHit = Boolean(responseData.is_cache_hit) || reportedRoute === "cache";
    const modelUsed = cacheHit ? "semantic_cache" : responseData.model || (fallbackRoute === "strong" ? "main" : "mini");
    const liveRoute = cacheHit ? "cache" : isMainModel(modelUsed) ? "strong" : reportedRoute;
    const llmResponseTimeMs = cacheHit ? 0 : responseData.llm_response_time_ms ?? responseData.llm_latency_ms;
    const normalizedRouterLatencyMs = responseData.router_latency_ms ?? (typeof latencyMs === "number" ? Math.max(latencyMs - (llmResponseTimeMs ?? 0), 0) : latencyMs);
    const baselineStrongCost = calculateCost("strong", tokensIn, tokensOut);
    // Keep demo pricing internally consistent: savings compare route pricing vs a strong-model baseline.
    const actualCost = cacheHit
        ? 0
        : calculateCost(liveRoute === "strong" ? "strong" : "weak", tokensIn, tokensOut);

    return {
        requestId: responseData.request_id,
        response: responseText,
        selectedRoute: liveRoute,
        cacheHit,
        modelUsed,
        tokensIn,
        tokensOut,
        actualCost,
        baselineStrongCost,
        routerLatencyMs: normalizedRouterLatencyMs,
        llmResponseTimeMs,
        strategy: responseData.strategy,
    };
};

const buildTelemetryEvent = (
    scenarioId: string,
    stepId: string,
    route: RouteType,
    cacheHit: boolean,
    modelUsed: string,
    tokensIn: number,
    tokensOut: number,
    actualCost: number,
    baselineStrongCost: number
): TelemetryEvent => {
    const savings = calculateSavings(actualCost, baselineStrongCost);

    return {
        scenario_id: scenarioId,
        step_id: stepId,
        selected_route: route,
        cache_hit: cacheHit,
        model_used: modelUsed,
        tokens_in: tokensIn,
        tokens_out: tokensOut,
        actual_cost: actualCost,
        baseline_strong_cost: baselineStrongCost,
        savings_absolute: savings.absolute,
        savings_percent: savings.percent,
    };
};

const RouterDemo: React.FC = () => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [customPrompt, setCustomPrompt] = useState(PREDEFINED_STEPS[0].request);
    const [presetResult, setPresetResult] = useState<CustomRunResult | null>(null);
    const [customStatus, setCustomStatus] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>(CUSTOM_INITIAL_CHAT_MESSAGES);
    const customCache = useRef<Map<string, CustomRunResult>>(new Map());
    const chatViewportRef = useRef<HTMLDivElement | null>(null);
    const customPromptRef = useRef<HTMLTextAreaElement | null>(null);

    const resizePromptInput = () => {
        if (!customPromptRef.current) {
            return;
        }

        customPromptRef.current.style.height = "0px";
        customPromptRef.current.style.height = `${Math.min(customPromptRef.current.scrollHeight, 128)}px`;
    };

    const currentStep = PREDEFINED_STEPS[Math.min(currentStepIndex, PREDEFINED_STEPS.length - 1)] ?? null;

    const emitTelemetry = (eventPayload: TelemetryEvent) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "router_demo_step",
            ...eventPayload,
        });

        window.dispatchEvent(
            new CustomEvent("router_demo_step", {
                detail: eventPayload,
            })
        );
    };

    useEffect(() => {
        if (chatViewportRef.current) {
            chatViewportRef.current.scrollTop = chatViewportRef.current.scrollHeight;
        }
    }, [chatMessages, isSubmitting]);

    useEffect(() => {
        resizePromptInput();
    }, [customPrompt]);

    const appendAssistantChatMessage = (result: CustomRunResult) => {
        const savings = calculateSavings(result.actualCost, result.baselineStrongCost);
        setChatMessages((messages) => [
            ...messages,
            {
                id: `assistant-${Date.now()}`,
                role: "assistant",
                content: result.response,
                requestId: result.requestId,
                route: result.selectedRoute,
                cacheHit: result.cacheHit,
                modelUsed: result.modelUsed,
                actualCost: result.actualCost,
                savingsAbsolute: savings.absolute,
                savingsPercent: savings.percent,
                routerLatencyMs: result.routerLatencyMs,
                llmResponseTimeMs: result.llmResponseTimeMs,
                strategy: result.strategy,
            },
        ]);
    };

    const runCustomPrompt = async () => {
        const prompt = customPrompt.trim();
        if (!prompt) {
            setCustomStatus("Enter a prompt to continue.");
            return;
        }

        setIsSubmitting(true);
        setCustomStatus("");
        setChatMessages((messages) => [
            ...messages,
            {
                id: `user-${Date.now()}`,
                role: "user",
                content: prompt,
            },
        ]);

        if (currentStep) {
            const expectedPrompt = normalizePrompt(currentStep.request);
            if (normalizePrompt(prompt) !== expectedPrompt) {
                setChatMessages((messages) => [
                    ...messages,
                    buildGuidanceMessage(`Please send this exact question to continue: "${currentStep.request}"`),
                ]);
                setCustomStatus("Use the guided question to advance to the next scenario.");
                setIsSubmitting(false);
                return;
            }
        }

        const normalized = normalizePrompt(prompt);
        const liveOptions = currentStep?.skipCache ? { skip_cache: true } : undefined;

        const selectedRoute: RouteType = isComplexPrompt(prompt) ? "strong" : "weak";
        let fallbackMode = false;
        let requestId: string | undefined;
        let responseText = "";
        let usedModel = selectedRoute === "strong" ? "main" : "mini";
        let actualCost = 0;
        let tokensIn = estimateTokens(prompt);
        let tokensOut = 0;
        let finalRoute: RouteType = selectedRoute;
        let finalCacheHit = false;
        let routerLatencyMs = 0;
        let llmResponseTimeMs = 0;
        let baselineStrongCost = calculateCost("strong", tokensIn, tokensOut);
        let resultStrategy: string | undefined;
        let strategyRationale = selectedRoute === "strong"
            ? "Complexity keywords + request length exceeded weak-threshold heuristic."
            : "Short support query with low reasoning depth qualifies for weak optimization.";

        try {
            const requestStartedAt = performance.now();
            const responseData = await proxyDemoLlm(undefined, prompt, liveOptions);
            routerLatencyMs = performance.now() - requestStartedAt;
            const normalizedResult = normalizeLiveResult(responseData, prompt, selectedRoute, routerLatencyMs);
            requestId = normalizedResult.requestId;
            responseText = normalizedResult.response;
            usedModel = normalizedResult.modelUsed;
            actualCost = normalizedResult.actualCost;
            tokensIn = normalizedResult.tokensIn;
            tokensOut = normalizedResult.tokensOut;
            finalRoute = normalizedResult.selectedRoute;
            finalCacheHit = normalizedResult.cacheHit;
            baselineStrongCost = normalizedResult.baselineStrongCost;
            routerLatencyMs = normalizedResult.routerLatencyMs ?? routerLatencyMs;
            llmResponseTimeMs = normalizedResult.llmResponseTimeMs ?? llmResponseTimeMs;
            resultStrategy = normalizedResult.strategy;
            strategyRationale = `Live router strategy: ${responseData.strategy}`;
        } catch (error) {
            fallbackMode = true;
            responseText = shortenSupportReply(buildFallbackResponse(selectedRoute, prompt));
            tokensOut = estimateTokens(responseText);
            llmResponseTimeMs = 0;
            actualCost = calculateCost(selectedRoute, tokensIn, tokensOut);
            baselineStrongCost = calculateCost("strong", tokensIn, tokensOut);
            setCustomStatus("Fallback mode active: live router endpoint unavailable, showing deterministic mock response.");
        }
        const finalizedCost = finalCacheHit
            ? 0
            : actualCost > 0 && !fallbackMode
                ? actualCost
                : calculateCost(finalRoute === "strong" ? "strong" : "weak", tokensIn, tokensOut);

        const result: CustomRunResult = {
            requestId,
            request: prompt,
            response: responseText,
            selectedRoute: finalRoute,
            cacheHit: finalCacheHit,
            modelUsed: usedModel,
            tokensIn,
            tokensOut,
            actualCost: finalizedCost,
            baselineStrongCost,
            rationale: strategyRationale,
            fallbackMode,
            routerLatencyMs,
            llmResponseTimeMs,
            strategy: fallbackMode ? `fallback_${finalRoute}` : resultStrategy,
        };

        setPresetResult(result);

        appendAssistantChatMessage(result);
        customCache.current.set(normalized, result);
        emitTelemetry(
            buildTelemetryEvent(
                currentStep ? currentStep.scenarioId : "guided_request",
                currentStep ? currentStep.stepId : "guided_live",
                result.selectedRoute,
                result.cacheHit,
                result.modelUsed,
                result.tokensIn,
                result.tokensOut,
                result.actualCost,
                result.baselineStrongCost
            )
        );

        if (currentStep) {
            const nextStepIndex = Math.min(PREDEFINED_STEPS.length - 1, currentStepIndex + 1);
            setCurrentStepIndex(nextStepIndex);

            if (nextStepIndex < PREDEFINED_STEPS.length) {
                const nextQuestion = PREDEFINED_STEPS[nextStepIndex].request;
                setCustomPrompt(nextQuestion);
                if (nextStepIndex > currentStepIndex) {
                    setChatMessages((messages) => [
                        ...messages,
                        buildGuidanceMessage(`Next scenario ready. Send this question: "${nextQuestion}"`),
                    ]);
                    setCustomStatus(nextStepIndex === PREDEFINED_STEPS.length - 1 ? "Final scenario ready." : "Scenario advanced. Follow the next guidance note.");
                } else if (!fallbackMode) {
                    setCustomStatus("All guided scenarios completed.");
                }
            }
        }

        setIsSubmitting(false);
    };

    const handlePromptKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            void runCustomPrompt();
        }
    };

    const activeResult = presetResult ?? (currentStep ? toRunResult(currentStep) : null);
    const selectedTokensIn = activeResult?.tokensIn ?? 0;
    const selectedTokensOut = activeResult?.tokensOut ?? 0;
    const selectedTokensTotal = selectedTokensIn + selectedTokensOut;
    const cumulativeCost = chatMessages.reduce(
        (totals, message) => {
            if (message.role !== "assistant") {
                return totals;
            }

            totals.actual += message.actualCost ?? 0;
            totals.saved += message.savingsAbsolute ?? 0;
            return totals;
        },
        { actual: 0, saved: 0 }
    );
    const totalAmountSpent = cumulativeCost.actual;
    const totalAmountSaved = cumulativeCost.saved;
    const totalBaselineCost = totalAmountSpent + totalAmountSaved;
    const totalSavingsPercent = totalBaselineCost > 0 ? (totalAmountSaved / totalBaselineCost) * 100 : 0;

    return (
        <div className="w-full bg-background text-foreground">

            <div className="container mx-auto grid gap-4 px-4 py-4">
                <Card className="border-slate-200 bg-white/95 text-slate-900 shadow-xl shadow-emerald-900/5">
                    <CardContent className="space-y-4 pt-6">
                        {(
                            <div className="space-y-3 rounded-[24px] border border-[#e6e6e6] bg-[#f5f5f5] p-3 shadow-[0_20px_40px_-36px_rgba(0,0,0,0.45)]">
                                <div className="flex items-center justify-between rounded-2xl bg-white px-3 py-2 shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-white shadow-sm">
                                            <Bot className="h-3.5 w-3.5" />
                                        </span>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">SigLLM Assistant</p>
                                        <span className="text-[10px] text-slate-400">v1</span>
                                    </div>
                                </div>
                                <div
                                    ref={chatViewportRef}
                                    className="h-[250px] overflow-y-auto rounded-2xl border border-[#e1e1e1] bg-white p-3 md:h-[280px]"
                                >
                                    <div className="space-y-3">
                                        {chatMessages.map((message) => {
                                            const isUser = message.role === "user";
                                            const isGuidance = message.role === "guidance";
                                            const roleLabel = isUser ? "You" : isGuidance ? "Guidance" : "SigLLM Assistant";
                                            const RoleIcon = isUser ? UserRound : isGuidance ? Sparkles : Bot;
                                            const avatarClass = isUser
                                                ? "border-slate-900 bg-slate-900 text-white"
                                                : isGuidance
                                                    ? "border-indigo-200 bg-indigo-100 text-indigo-700"
                                                    : "border-slate-300 bg-white text-slate-700";

                                            return (
                                                <div key={message.id} className={`flex items-end gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
                                                    {!isUser && (
                                                        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${avatarClass}`}>
                                                            <RoleIcon className="h-4 w-4" />
                                                        </span>
                                                    )}
                                                    <div
                                                        className={`max-w-[82%] rounded-[18px] border px-4 py-3 shadow-sm ${isUser
                                                            ? "border-slate-200 bg-slate-900 text-white"
                                                            : isGuidance
                                                                ? "border-indigo-200 bg-indigo-50 text-indigo-900"
                                                                : "border-slate-200 bg-[#f8f8f8] text-slate-800"
                                                            }`}
                                                    >
                                                        <p className={`text-xs font-semibold uppercase tracking-wide ${isUser ? "text-slate-300" : isGuidance ? "text-indigo-700" : "text-slate-500"
                                                            }`}>
                                                            {roleLabel}
                                                        </p>
                                                        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed">{message.content}</p>
                                                        {message.role === "assistant" && message.route && (
                                                            <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                                                                <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 font-semibold text-emerald-800">
                                                                    Optimization: {getOptimizationLabel(message)}
                                                                </span>
                                                                {shouldShowModelLabel(message) && (
                                                                    <span className="rounded-full border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-600">
                                                                        LLM Used: {formatModelLabel(message.modelUsed)}
                                                                    </span>
                                                                )}
                                                                <span className="rounded-full border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-600">
                                                                    Actual Cost: {formatCurrency(message.actualCost ?? 0)}
                                                                </span>
                                                                <span className="rounded-full border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-600">
                                                                    Router Latency: {formatLatency(message.routerLatencyMs)}
                                                                </span>
                                                                <span className="rounded-full border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-600">
                                                                    LLM Response Time: {formatLatency(message.llmResponseTimeMs)}
                                                                </span>
                                                                <span className="rounded-full border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-600">
                                                                    Savings: {formatCurrency(message.savingsAbsolute ?? 0)} ({(message.savingsPercent ?? 0).toFixed(1)}%)
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {isUser && (
                                                        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${avatarClass}`}>
                                                            <RoleIcon className="h-4 w-4" />
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {isSubmitting && (
                                            <div className="flex items-end gap-2 justify-start">
                                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700">
                                                    <Bot className="h-4 w-4" />
                                                </span>
                                                <div className="max-w-[85%] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">SigLLM Assistant</p>
                                                    <div className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                                                        <span>Waiting for router response</span>
                                                        <span className="flex items-center gap-1" aria-hidden="true">
                                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce [animation-delay:-0.3s]" />
                                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce [animation-delay:-0.15s]" />
                                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce" />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-slate-300 bg-white p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                                    <label className="sr-only" htmlFor="custom-prompt">
                                        Custom support request
                                    </label>
                                    <textarea
                                        id="custom-prompt"
                                        ref={customPromptRef}
                                        rows={1}
                                        className="max-h-32 w-full resize-none overflow-y-auto border-0 bg-transparent p-0 text-sm leading-6 text-slate-900 outline-none focus:ring-0"
                                        placeholder="Send the guided message..."
                                        value={customPrompt}
                                        onChange={(event) => setCustomPrompt(event.target.value)}
                                        onKeyDown={handlePromptKeyDown}
                                        disabled={isSubmitting}
                                    />
                                    <div className="mt-3 flex items-center justify-end gap-2">
                                        <Button
                                            onClick={runCustomPrompt}
                                            disabled={isSubmitting || customPrompt.trim().length === 0}
                                            className="h-9 gap-2 rounded-full bg-slate-900 px-4 text-xs font-semibold uppercase tracking-wide text-white hover:bg-slate-800"
                                            aria-label="Send message"
                                        >
                                            <SendHorizontal className="h-3.5 w-3.5" />
                                            {isSubmitting ? "Sending..." : "Send message"}
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500">
                                    {customStatus}
                                </p>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2">
                            <Badge className="border-sky-300 bg-sky-100 text-sky-900">
                                Tokens Used: {selectedTokensTotal} ({selectedTokensIn} in / {selectedTokensOut} out)
                            </Badge>
                            <Badge className="border-emerald-400 bg-emerald-200 text-emerald-950">
                                Total Amount Spent: {formatCurrency(totalAmountSpent)}
                            </Badge>
                            <Badge className="border-lime-400 bg-lime-200 text-lime-950">
                                Cost Saved: {formatCurrency(totalAmountSaved)} ({totalSavingsPercent.toFixed(1)}%)
                            </Badge>
                        </div>
                    </CardContent>
                </Card>

            </div>
        </div>
    );
};

export default RouterDemo;
